import { ApplicationOpenCloseDates } from '@/models/Scans'
import { tzOffset } from '@date-fns/tz'
import { addHours, format } from 'date-fns'

// Constants
export const CLOSING_SOON_THRESHOLD_DAYS = 7
const MS_PER_DAY = 1000 * 60 * 60 * 24

// Backend timestamps are `timestamp without time zone` (stored as UTC wall-clock)
// and serialize with no trailing 'Z'/offset, which JS would otherwise parse as local time.
export const parseUTCDate = (dateInput: string | Date): Date => {
  if (typeof dateInput === 'string') {
    return new Date(dateInput.includes('Z') ? dateInput : dateInput + 'Z')
  }
  return dateInput
}

export const localisedDate = (date: Date, formatStr = 'dd MMMM yyyy hh:mm a') => {
  return format(
    addHours(
      date,
      tzOffset(Intl.DateTimeFormat().resolvedOptions().timeZone, date) / 60,
    ),
    formatStr,
  )
}

// A discriminated union rather than independent optional fields: whenever
// `status` is 'closed'/'closing-soon', `message` is guaranteed present, so a
// caller narrowing on `status` gets a non-null `message` for free. `message`
// can still be non-null even when `status` is null (e.g. a close date beyond
// the closing-soon threshold, or an always-open opportunity) - callers that
// only want to react to a noteworthy status (badges, alerts) should check
// `status` first rather than treating a truthy `message` as one.
// `isEstimate` mirrors the current round's own estimate_date_flag (false
// when no dated round backs the message, e.g. closed/always-open) - it's a
// flag for the caller to render its own "Estimate Only" treatment with,
// matching the pattern already established in MatchResultsListItem.tsx and
// OnboardingMatchRow.tsx, rather than baked into `message` itself.
type OpportunityDisplayInfo =
  | { status: 'closed' | 'closing-soon'; message: string; isEstimate: boolean }
  | { status: null; message: string | null; isEstimate: boolean }

/**
 * Normalize date to user's local timezone
 * Matches the logic in MatchResults.tsx for consistency
 */
const normaliseToLocal = (
  date: string | Date | null,
  timeZone: string
): Date | null => {
  if (!date) return null
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return new Date(dateObj.toLocaleString('en-US', { timeZone }))
}

/**
 * Gets comprehensive display information for an opportunity's status
 * Combines status determination and message generation to avoid redundant calculations
 *
 * Note: Aligns with MatchResults.tsx logic:
 * - Treats null close dates as open-ended (not closed)
 * - Only shows status for currently open rounds (between open and close dates)
 * - Uses timezone normalization for consistency
 */
export const getOpportunityDisplayInfo = (
  opportunity: { always_open: boolean },
  dates: ApplicationOpenCloseDates[],
  userTimeZone?: string
): OpportunityDisplayInfo => {
  // Use provided timezone or fallback to browser's timezone
  const timeZone = userTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone

  // Get today in user's timezone
  const today = new Date()
  const localToday = normaliseToLocal(today, timeZone)!
  localToday.setHours(0, 0, 0, 0)

  // Find currently open round (between open and close dates)
  const currentRound = dates
    ?.filter((d) => {
      const normalizedOpen = d.application_open
        ? normaliseToLocal(d.application_open, timeZone)
        : localToday // Treat null open as already open

      const normalizedClose = normaliseToLocal(d.application_close, timeZone)

      // Round is current if:
      // - Open date has passed (or is null)
      // - Close date hasn't passed yet (or is null/open-ended)
      return (
        normalizedOpen &&
        normalizedOpen <= localToday &&
        (!normalizedClose || normalizedClose >= localToday)
      )
    })
    .sort((a, b) => {
      // Sort by close date, with null (open-ended) coming last
      if (!a.application_close) return 1
      if (!b.application_close) return -1
      return (
        new Date(a.application_close).getTime() -
        new Date(b.application_close).getTime()
      )
    })[0]

  // If no currently open round exists
  if (!currentRound) {
    // Name always-open opportunities explicitly rather than showing nothing.
    if (opportunity.always_open) {
      return {
        status: null,
        message: 'This opportunity is open year-round.',
        isEstimate: false,
      }
    }

    // Check if there are any future rounds
    const hasFutureRounds = dates?.some((d) => {
      const normalizedOpen = d.application_open
        ? normaliseToLocal(d.application_open, timeZone)
        : null
      return normalizedOpen && normalizedOpen > localToday
    })

    // If has future rounds, don't show closed status
    if (hasFutureRounds) {
      return { status: null, message: null, isEstimate: false }
    }

    // Otherwise, all rounds have closed
    return {
      status: 'closed',
      message: 'This opportunity has closed.',
      isEstimate: false,
    }
  }

  // If current round has no close date, it's open-ended (no status to show,
  // but still name it for always-open opportunities). No date is presented,
  // so there's nothing to mark as an estimate.
  if (!currentRound.application_close) {
    return {
      status: null,
      message: opportunity.always_open
        ? 'This opportunity is open year-round.'
        : null,
      isEstimate: false,
    }
  }

  const closeDate = normaliseToLocal(currentRound.application_close, timeZone)!
  closeDate.setHours(0, 0, 0, 0)

  // Calculate days until close
  const daysUntilClose = Math.ceil(
    (closeDate.getTime() - localToday.getTime()) / MS_PER_DAY
  )

  // "closing-soon" status (drives the badge) stays threshold-gated, but the
  // message itself is always returned once a current round's close date is
  // known — the date should be visible regardless of how far away it is.
  const formattedDate = closeDate.toLocaleDateString('en-NZ', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
  const daysText =
    daysUntilClose === 0
      ? 'today'
      : `in ${daysUntilClose} ${daysUntilClose === 1 ? 'day' : 'days'}`

  const message = opportunity.always_open
    ? `This opportunity's current round closes ${daysText} on ${formattedDate}.`
    : `This opportunity closes ${daysText} on ${formattedDate}.`

  const isEstimate = currentRound.estimate_date_flag || false

  if (daysUntilClose <= CLOSING_SOON_THRESHOLD_DAYS) {
    return { status: 'closing-soon', message, isEstimate }
  }
  return { status: null, message, isEstimate }
}
